/* ------------------------------------------------------------------------------
 *
 *  # Lead Management Components
 *
 *  Lead grid and related functionality using DataTables
 *
 * ---------------------------------------------------------------------------- */

import { 
	initGenericDataTable, 
	refreshDataTable, 
	destroyDataTable 
} from '../integrations/datatables.js';

// Lead Grid Variables
let leadGridCallbacks = null;

// Helper functions
function getInitials(lead) {
	const first = lead.parentFirstName ? lead.parentFirstName[0].toUpperCase() : '';
	const last = lead.parentLastName ? lead.parentLastName[0].toUpperCase() : '';
	return `${first}${last}`;
}

function getEstadoBadgeClass(estadoNombre) {
	switch(estadoNombre) {
		case 'Admitido': return 'bg-success';
		case 'No admitido': return 'bg-danger';
		case 'Visitado': return 'bg-info';
		case 'Visita Programada': return 'bg-warning';
		case 'Contactado': return 'bg-primary';
		case 'No interesado': return 'text-white';
		case 'Interesado': return 'text-white';
		case 'Nuevo': return 'bg-secondary';
		default: return 'bg-secondary';
	}
}

function getEstadoBadgeStyle(estadoNombre) {
	switch(estadoNombre) {
		case 'No interesado': return 'background-color: #FFA500;';
		case 'Interesado': return 'background-color: #6A5ACD;';
		default: return '';
	}
}

function getDiasSinContacto(createdAt) {
	if (!createdAt) return '-';
	
	const date = new Date(createdAt);
	const now = new Date();
	const diffMs = now - date;
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	
	if (diffDays === 0) return 'Hoy';
	if (diffDays === 1) return 'Ayer';
	return `Hace ${diffDays} días`;
}

function formatFechaVisita(fecha, hora) {
	if (!fecha) return '-';
	
	const date = new Date(fecha);
	const dateStr = date.toLocaleDateString('es-ES');
	
	if (hora) {
		return `${dateStr} ${hora}`;
	}
	return dateStr;
}

// Lead-specific DataTable Functions
export function initLeadGrid(leads) {
	console.log('initLeadGrid called with:', leads);
	
	destroyDataTable('leadsTable');
	
	// Validación defensiva
	if (!leads || !Array.isArray(leads)) {
		console.error('initLeadGrid: leads parameter is not an array', leads);
		
		// Inicializar tabla vacía con estructura
		const options = {
			data: [],
			columns: [
				{ title: 'Acciones', orderable: false, className: 'text-center no-export', width: '120px' },
				{ title: 'Familia', width: '22%' },
				{ title: 'Niño/a', width: '18%' },
				{ title: 'Estado', className: 'text-center', width: '12%' },
				{ title: 'Ambiente', width: '12%' },
				{ title: 'Visitas', className: 'text-center', width: '15%' },
				{ title: 'Solicitud', width: '10%' },
				{ title: 'Conversión', className: 'text-center', width: '11%' }
			],
			exportTitle: 'Panel de Leads - Montessori Nova School',
			order: [[6, 'desc']]
		};
		return initGenericDataTable('leadsTable', options);
	}
	
	if (leads.length === 0) {
		console.warn('initLeadGrid: leads array is empty');
	}
	
	const tableData = leads.map(lead => [
		generateLeadActions(lead.id),
		generateLeadColumn(lead),
		generateChildColumn(lead),
		generateEstadoColumn(lead),
		generateAmbienteColumn(lead),
		generateVisitasColumn(lead),
		getDiasSinContacto(lead.createdAt),
		lead.estaConvertido 
			? '<span class="badge bg-success"><i class="ph-check-circle me-1"></i>Convertido</span>' 
			: '<span class="badge bg-warning"><i class="ph-hourglass me-1"></i>Pendiente</span>'
	]);

	const options = {
		data: tableData,
		columns: [
			{ title: 'Acciones', orderable: false, className: 'text-center no-export', width: '120px' },
			{ title: 'Familia', width: '22%' },
			{ title: 'Niño/a', width: '18%' },
			{ title: 'Estado', className: 'text-center', width: '12%' },
			{ title: 'Ambiente', width: '12%' },
			{ title: 'Visitas', className: 'text-center', width: '15%' },
			{ title: 'Solicitud', width: '10%' },
			{ title: 'Conversión', className: 'text-center', width: '11%' }
		],
		exportTitle: 'Panel de Leads - Montessori Nova School',
		order: [[6, 'desc']], // Order by created date
		buttons: [
			{
				extend: 'collection',
				className: 'btn btn-flat-primary btn-labeled btn-labeled-start dropdown-toggle me-3 rounded',
				text: '<span class="btn-labeled-icon bg-primary text-white rounded-start"><i class="ph-export"></i></span>Exportar',
				buttons: [
					{
						extend: 'excel',
						text: '<i class="ph-file-xls me-1"></i>Excel',
						className: 'dropdown-item',
						title: 'Panel de Leads - Montessori Nova',
						exportOptions: {
							columns: [1, 2, 3, 4, 5, 6, 7],
							format: {
								body: function (inner, coldex, rowdex) {
									if (!inner || (typeof inner === 'string' && !inner.includes('<'))) {
										return inner;
									}
									const $el = $(inner);
									const exportText = $el.attr('data-export');
									if (exportText) {
										return exportText;
									}
									if (inner.length <= 0) return inner;
									let el = $.parseHTML(inner);
									let result = '';
									$.each(el, function (index, item) {
										if (item.innerText === undefined) {
											result = result + item.textContent;
										} else {
											result = result + item.innerText;
										}
									});
									return result;
								}
							}
						}
					},
					{
						extend: 'copy',
						text: '<i class="ph-clipboard-text me-1"></i>Copiar',
						className: 'dropdown-item',
						exportOptions: {
							columns: [1, 2, 3, 4, 5, 6, 7],
							format: {
								body: function (inner, coldex, rowdex) {
									if (!inner || (typeof inner === 'string' && !inner.includes('<'))) {
										return inner;
									}
									const $el = $(inner);
									const exportText = $el.attr('data-export');
									if (exportText) {
										return exportText;
									}
									if (inner.length <= 0) return inner;
									let el = $.parseHTML(inner);
									let result = '';
									$.each(el, function (index, item) {
										if (item.innerText === undefined) {
											result = result + item.textContent;
										} else {
											result = result + item.innerText;
										}
									});
									return result;
								}
							}
						}
					}
				]
			}
		]
	};

	return initGenericDataTable('leadsTable', options);
}

function generateLeadActions(leadId) {
	return `
		<div class="d-inline-flex">
			<a href="javascript:void(0)" class="text-body me-2" onclick="window.sfBlazorSB.leads.editLead(${leadId})" title="Editar lead">
				<i class="ph-pencil"></i>
			</a>
			<a href="javascript:void(0)" class="text-primary me-2" onclick="window.sfBlazorSB.leads.programarVisita(${leadId})" title="Programar visita">
				<i class="ph-calendar-check"></i>
			</a>
			<a href="javascript:void(0)" class="text-body" onclick="window.sfBlazorSB.leads.deleteLead(${leadId})" title="Eliminar lead">
				<i class="ph-trash"></i>
			</a>
		</div>
	`;
}

function generateLeadColumn(lead) {
	const fullName = lead.parentFullName || `${lead.parentFirstName} ${lead.parentLastName}`;
	const initials = getInitials(lead);
	const exportText = [initials, fullName, lead.email, lead.phone].filter(Boolean).join(' - ');
	
	const profileImage = `<div class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style="width: 40px; height: 40px; font-weight: 600;">${initials}</div>`;
	
	return `
		<div class="d-flex align-items-center" data-export="${exportText.replace(/"/g, '&quot;')}">
			<div class="me-3">${profileImage}</div>
			<div>
				<div class="fw-semibold">${fullName}</div>
				<div class="text-muted fs-sm">${lead.email}</div>
				<div class="text-muted fs-sm"><i class="ph-phone ph-xs me-1"></i>${lead.phone}</div>
			</div>
		</div>
	`;
}

function generateChildColumn(lead) {
	const exportText = `${lead.childName} - ${lead.childAge}`;
	
	return `
		<div data-export="${exportText}">
			<div class="fw-semibold"><i class="ph-baby me-1"></i>${lead.childName}</div>
			<div class="text-muted fs-sm">${lead.childAge}</div>
		</div>
	`;
}

function generateEstadoColumn(lead) {
	const badgeClass = getEstadoBadgeClass(lead.estadoLeadNombre);
	const badgeStyle = getEstadoBadgeStyle(lead.estadoLeadNombre);
	const styleAttr = badgeStyle ? ` style="${badgeStyle}"` : '';
	return `<span class="badge ${badgeClass}"${styleAttr} data-export="${lead.estadoLeadNombre}">${lead.estadoLeadNombre}</span>`;
}

function generateAmbienteColumn(lead) {
	if (!lead.ambienteNombre) {
		return '<span class="text-muted" data-export="-">Sin asignar</span>';
	}
	return `<span class="badge bg-info badge-outline" data-export="${lead.ambienteNombre}">${lead.ambienteNombre}</span>`;
}

function generateVisitasColumn(lead) {
	const visita1 = formatFechaVisita(lead.fechaVisita1, lead.horaVisita1);
	const visita2 = formatFechaVisita(lead.fechaVisita2, lead.horaVisita2);
	
	let html = '';
	let exportText = '';
	
	if (visita1 !== '-') {
		html += `<div class="text-muted fs-sm"><i class="ph-calendar-check ph-xs me-1"></i>V1: ${visita1}</div>`;
		exportText += `V1: ${visita1}`;
	}
	
	if (visita2 !== '-') {
		html += `<div class="text-muted fs-sm"><i class="ph-calendar-check ph-xs me-1"></i>V2: ${visita2}</div>`;
		exportText += (exportText ? ' | ' : '') + `V2: ${visita2}`;
	}
	
	if (!html) {
		html = '<span class="text-muted">Sin programar</span>';
		exportText = 'Sin programar';
	}
	
	return `<div data-export="${exportText}">${html}</div>`;
}

// Refresh lead grid specifically
export function refreshLeadGrid(leads) {
	console.log('refreshLeadGrid called with:', leads);
	destroyDataTable('leadsTable');
	initLeadGrid(leads);
}

// Set callbacks for lead grid
export function setLeadCallbacks(dotNetRef) {
	leadGridCallbacks = dotNetRef;
}

// Lead action functions
export function viewLead(leadId) {
	if (leadGridCallbacks) {
		leadGridCallbacks.invokeMethodAsync('ViewLead', leadId);
	}
}

export function editLead(leadId) {
	if (leadGridCallbacks) {
		leadGridCallbacks.invokeMethodAsync('EditLead', leadId);
	}
}

export function deleteLead(leadId) {
	if (leadGridCallbacks) {
		leadGridCallbacks.invokeMethodAsync('DeleteLead', leadId);
	}
}

export function programarVisita(leadId) {
	if (leadGridCallbacks) {
		leadGridCallbacks.invokeMethodAsync('ProgramarVisita', leadId);
	}
}

// Apply filters function
export function applyLeadFilters() {
	const estadoFilter = document.getElementById('estadoFilter')?.value || '';
	const ambienteFilter = document.getElementById('ambienteFilterLeads')?.value || '';
	const visitaFilter = document.getElementById('visitaFilter')?.value || '';
	const globalSearch = document.getElementById('globalSearchLeads')?.value || '';

	const table = $('#leadsTable').DataTable();

	// Clear previous search functions
	$.fn.dataTable.ext.search.splice(0);

	// Add custom search functions
	if (estadoFilter) {
		$.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
			return data[3].includes(estadoFilter);
		});
	}

	if (ambienteFilter) {
		$.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
			return data[4].toLowerCase().includes(ambienteFilter.toLowerCase());
		});
	}

	if (visitaFilter) {
		$.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
			const visitasText = data[5];
			if (visitaFilter === 'programadas') {
				return !visitasText.includes('Sin programar');
			} else if (visitaFilter === 'sin-programar') {
				return visitasText.includes('Sin programar');
			}
			return true;
		});
	}

	// Apply global search
	table.search(globalSearch).draw();
}

// Clear filters function
export function clearLeadFilters() {
	const estadoFilter = document.getElementById('estadoFilter');
	const ambienteFilter = document.getElementById('ambienteFilterLeads');
	const visitaFilter = document.getElementById('visitaFilter');
	const globalSearch = document.getElementById('globalSearchLeads');

	if (estadoFilter) estadoFilter.value = '';
	if (ambienteFilter) ambienteFilter.value = '';
	if (visitaFilter) visitaFilter.value = '';
	if (globalSearch) globalSearch.value = '';

	const table = $('#leadsTable').DataTable();
	$.fn.dataTable.ext.search.splice(0);
	table.search('').draw();
}

